import type { ChangeEvent, FocusEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import validate from 'validate.js';
import { ApiError, TotpService } from 'api';
import {
  CONFIRMATION_BUTTON_BACK,
  CONFIRMATION_BUTTON_NEXT,
  INVALID_OTP_ERROR,
  KEY_COPIED,
  MESSAGE_BOX_TITLE_SUCCESS,
  MISSING_BACKUP_CODE,
  RESULT_SUCCESS,
  SUCCESS_INSIDE,
} from 'common/constants/auth';
import { ERROR_OCCURRED } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import type { AuthenticationInputFormState } from 'pages/Auth/components/AuthenticationInput';
import type { BackupCodesCloseHandler } from 'pages/Auth/components/BackupCodes';
import { UNKNOWN_OTP_ERROR_MESSAGE } from 'pages/Auth/components/OTP/OTP.constants';
import type { OTPProps } from 'pages/Auth/components/OTP/OTP.props';
import type { MessageBoxState, OTPResponse } from 'pages/Auth/components/OTP/OTP.types';
import { getOtpUriParams, normalizeBackupCodeValue, normalizeOtpValue } from 'pages/Auth/components/OTP/OTP.utils';
import { constraints, constraintsBkCode } from 'pages/Auth/components/OTP/OTP.validation';
import { useLogOutUser } from 'pages/Auth/hooks';
import {
  clearPendingBackupCodes,
  clearRememberedTwoFactor,
  getPendingBackupCodes,
  setOtpVerified,
  setPendingBackupCodes,
  setRememberedTwoFactor,
} from 'pages/Auth/utils';
import { useResponse } from 'services/hooks/useResponse';
import { extractServerErrorMessage, Logger } from 'utilities';

type OtpFlowStep = 'result' | 'setup' | 'verify' | null;

interface UseOtpFlowResult {
  backupCodes: string[];
  currentStep: OtpFlowStep;
  formState: AuthenticationInputFormState;
  hasBackupCodes: boolean;
  handleCancelSetup: () => void;
  handleBackupCodesAcknowledge: BackupCodesCloseHandler;
  handleCopyToClipboard: () => Promise<void>;
  handleInputBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  handleToggleOtpKey: () => void;
  isCodeWorking: boolean;
  isCompleting: boolean;
  isGeneratingBackupCodes: boolean;
  isSubmitting: boolean;
  messageBoxData: MessageBoxState;
  otpData?: OTPResponse;
  otpSecretKey?: string;
  rememberThisComputer: boolean;
  showRememberThisComputer: boolean;
  showOtpKey: boolean;
  startBackupCodeFlow: () => void;
  username?: string;
  generateBackupCodes: () => Promise<boolean>;
  logOutUser: () => void;
  returnToOtpVerificationFlow: () => void;
  setRememberThisComputer: (remember: boolean) => void;
}

const createDefaultMessageBoxState = (
  onBack: () => void,
  onNext: (() => void) | undefined
): MessageBoxState => ({
  show: false,
  title: MESSAGE_BOX_TITLE_SUCCESS,
  result: RESULT_SUCCESS,
  backBtnText: CONFIRMATION_BUTTON_BACK,
  nextBtnText: CONFIRMATION_BUTTON_NEXT,
  type: SUCCESS_INSIDE,
  success: true,
  actionBack: onBack,
  actionNext: () => onNext?.(),
});

const initialFormState: AuthenticationInputFormState = {
  dbErrors: {},
  errors: {},
  isValid: false,
  touched: {},
  values: {},
};

const useOtpFlow = ({
  setDeviceData,
  setDisplayReauthenticate,
  showActionButtons = true,
  userHas2FA,
  verifyOtp,
}: OTPProps): UseOtpFlowResult => {
  const [{ user }] = useStore() as UseStoreValues;
  const logOutUser = useLogOutUser();
  const { successNotification } = useResponse();
  const [formState, setFormState] = useState<AuthenticationInputFormState>(initialFormState);
  const defaultMessageBoxState = useMemo(
    () => createDefaultMessageBoxState(logOutUser, () => verifyOtp?.(true, 'verified')),
    [logOutUser, verifyOtp]
  );
  // Backup codes are only shown once, so keep them refresh-safe until the user acknowledges them.
  const [restoredPendingBackupCodes] = useState(() => getPendingBackupCodes());

  const [otpData, setOtpData] = useState<OTPResponse>();
  const [otpSecretKey, setOtpSecretKey] = useState<string>();
  const [showOtpKey, setShowOtpKey] = useState(false);
  const [rememberThisComputer, setRememberThisComputer] = useState(false);
  const [messageBoxData, setMessageBoxData] = useState<MessageBoxState>(defaultMessageBoxState);
  const [isCodeWorking, setIsCodeWorking] = useState(false);
  const [showBackupCodesStep, setShowBackupCodesStep] = useState(() => Boolean(restoredPendingBackupCodes?.length));
  const [backupCodes, setBackupCodes] = useState<string[]>(() => restoredPendingBackupCodes ?? []);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isGeneratingBackupCodes, setIsGeneratingBackupCodes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldCompleteAfterBackupCodesClose, setShouldCompleteAfterBackupCodesClose] = useState(
    () => Boolean(restoredPendingBackupCodes?.length)
  );
  const showRememberThisComputer = userHas2FA;

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    let nextValue = event.target.value;

    if (event.target.name === 'otp') {
      nextValue = normalizeOtpValue(event.target.value);
    } else if (event.target.name === 'backupCode') {
      nextValue = normalizeBackupCodeValue(event.target.value);
    }

    setFormState(currentFormState => ({
      ...currentFormState,
      dbErrors: {},
      values: {
        [event.target.name]: nextValue,
      },
    }));
  }, []);

  const handleInputBlur = useCallback<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(event => {
    const { name } = event.target;

    setFormState(currentFormState => ({
      ...currentFormState,
      touched: {
        ...currentFormState.touched,
        [name]: true,
      },
    }));
  }, []);

  const completeVerifiedOtp = useCallback(() => {
    if (verifyOtp) {
      setIsCompleting(true);
    }

    if (userHas2FA && !isCodeWorking && user?.id) {
      if (showRememberThisComputer && rememberThisComputer) {
        setRememberedTwoFactor(user.id);
      } else {
        clearRememberedTwoFactor(user.id);
      }
    }

    verifyOtp?.(true, 'verified');
    setOtpVerified();
  }, [isCodeWorking, rememberThisComputer, showRememberThisComputer, user?.id, userHas2FA, verifyOtp]);

  const verifyBackupCode = useCallback(async () => {
    const { backupCode } = formState.values;
    if (!backupCode) {
      setFormState(prevFormState => ({
        ...prevFormState,
        errors: {
          backupCode: MISSING_BACKUP_CODE,
        },
      }));
      return;
    }

    try {
      const response = await TotpService.apiTotpStaticVerifyCreate(backupCode as string);
      if (response) {
        completeVerifiedOtp();
      }
    } catch (error: unknown) {
      const errorMsg = extractServerErrorMessage(error) ?? ERROR_OCCURRED;
      setFormState(prevFormState => ({
        ...prevFormState,
        dbErrors: {
          backupCode: [errorMsg],
        },
      }));
    }
  }, [completeVerifiedOtp, formState.values]);

  const generateBackupCodes = useCallback(async () => {
    setIsGeneratingBackupCodes(true);

    try {
      const response = await TotpService.apiTotpStaticCreateCreate();

      if (response) {
        setBackupCodes(response.tokens);
        setPendingBackupCodes(response.tokens);
        setShowBackupCodesStep(true);
        return true;
      }
    } catch (error: unknown) {
      // A 403 means this sensitive backup-code action needs a fresh user reauthentication.
      if (error instanceof ApiError && error.status === 403 && setDisplayReauthenticate) {
        setDisplayReauthenticate(true);
      }
    } finally {
      setIsGeneratingBackupCodes(false);
    }

    return false;
  }, [setDisplayReauthenticate]);

  const validateOtp = useCallback(
    async ({ showMessageBox }: { showMessageBox: boolean }) => {
      try {
        const response = await TotpService.apiTotpVerifyCreate(formState.values.otp as string);

        if (response?.verified) {
          if (setDeviceData) {
            setDeviceData(prevDeviceData => ({
              ...prevDeviceData,
              confirmed: true,
            }));
          }

          const shouldGenerateBackupCodes = !userHas2FA || !user?.has_tokens_2fa;
          if (shouldGenerateBackupCodes) {
            // Keep the OTP step mounted until the new backup codes are ready so setup does not flash
            // an intermediate success surface before we can hand off to the codes screen.
            const openedBackupCodesStep = await generateBackupCodes();

            if (openedBackupCodesStep) {
              if (showMessageBox) {
                setMessageBoxData({
                  ...defaultMessageBoxState,
                  show: true,
                });
              } else {
                setShouldCompleteAfterBackupCodesClose(true);
              }
              return;
            }
          }

          setMessageBoxData({
            ...defaultMessageBoxState,
            show: showMessageBox,
          });

          if (!showMessageBox) {
            completeVerifiedOtp();
          }
        } else {
          setFormState(prevFormState => ({
            ...prevFormState,
            dbErrors: {
              otp: [INVALID_OTP_ERROR],
            },
          }));
        }
      } catch (error: unknown) {
        const errorMsg = extractServerErrorMessage(error) ?? ERROR_OCCURRED;
        setFormState(prevFormState => ({
          ...prevFormState,
          dbErrors: {
            otp: [errorMsg],
          },
        }));
      }
    },
    [
      defaultMessageBoxState,
      formState.values.otp,
      generateBackupCodes,
      completeVerifiedOtp,
      setDeviceData,
      user?.has_tokens_2fa,
      userHas2FA,
    ]
  );

  useEffect(() => {
    let isMounted = true;

    const generateOrReturnUserTOTP = async () => {
      try {
        const response = await TotpService.apiTotpCreateList();
        if (!isMounted) {
          return;
        }
        setOtpData(response);
        const otpUriParams = getOtpUriParams(response.url);
        setOtpSecretKey(otpUriParams?.secret ?? '');
      } catch (error) {
        Logger.error('useOtpFlow.generateOrReturnUserTOTP', error instanceof Error ? error : UNKNOWN_OTP_ERROR_MESSAGE);
      }
    };

    if (!userHas2FA) {
      generateOrReturnUserTOTP();
    }

    return () => {
      isMounted = false;
    };
  }, [userHas2FA]);

  useEffect(() => {
    if (otpData && setDeviceData) {
      const { persistent_id: persistentId } = otpData;
      setDeviceData(prevDeviceData => ({
        ...prevDeviceData,
        persistent_id: persistentId,
      }));
    }
  }, [otpData, setDeviceData]);

  useEffect(() => {
    const errors = validate(formState.values, !isCodeWorking ? constraints : constraintsBkCode);
    setFormState(currentFormState => ({
      ...currentFormState,
      errors: errors ?? {},
      isValid: !errors,
    }));
  }, [formState.values, isCodeWorking]);

  const handleBackupCodesAcknowledge: BackupCodesCloseHandler = useCallback(() => {
    const shouldKeepBackupCodesUntilAuthRouteExit = showActionButtons && verifyOtp;
    const completeAfterBackupCodes = () => {
      if (!shouldKeepBackupCodesUntilAuthRouteExit) {
        clearPendingBackupCodes();
      }

      completeVerifiedOtp();
    };

    if (shouldCompleteAfterBackupCodesClose) {
      setShouldCompleteAfterBackupCodesClose(false);
      completeAfterBackupCodes();
      return;
    }

    // The sign-in setup flow should continue immediately after the user acknowledges
    // their new backup codes, instead of dropping them back into OTP verification again.
    if (messageBoxData.show && verifyOtp) {
      completeAfterBackupCodes();
      return;
    }

    clearPendingBackupCodes();
    setShowBackupCodesStep(false);
  }, [completeVerifiedOtp, messageBoxData.show, shouldCompleteAfterBackupCodesClose, showActionButtons, verifyOtp]);

  const handleToggleOtpKey = useCallback(() => {
    setShowOtpKey(currentValue => !currentValue);
  }, []);

  const handleCopyToClipboard = useCallback(async () => {
    try {
      if (otpSecretKey) {
        await navigator.clipboard.writeText(otpSecretKey);
        successNotification(KEY_COPIED);
      }
    } catch (error) {
      Logger.error('useOtpFlow.handleCopyToClipboard', error instanceof Error ? error : UNKNOWN_OTP_ERROR_MESSAGE);
    }
  }, [otpSecretKey, successNotification]);

  const handleCancelSetup = useCallback(() => {
    clearPendingBackupCodes();
    logOutUser();
  }, [logOutUser]);

  const returnToOtpVerificationFlow = useCallback(() => {
    setIsCodeWorking(false);
    setFormState(initialFormState);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (!userHas2FA) {
        await validateOtp({ showMessageBox: true });
        return;
      }

      if (!isCodeWorking) {
        await validateOtp({ showMessageBox: false });
        return;
      }

      await verifyBackupCode();
    } finally {
      setIsSubmitting(false);
    }
  }, [isCodeWorking, isSubmitting, userHas2FA, validateOtp, verifyBackupCode]);

  const currentStep = useMemo<OtpFlowStep>(() => {
    // Freshly generated backup codes must stay on the success step until the user closes that handoff,
    // even if the surrounding auth shell already recognizes the device as configured.
    if (showBackupCodesStep || messageBoxData.show) {
      return 'result';
    }

    if (userHas2FA) {
      return 'verify';
    }

    if (otpData) {
      return 'setup';
    }

    return null;
  }, [messageBoxData.show, otpData, showBackupCodesStep, userHas2FA]);

  const startBackupCodeFlow = useCallback(() => {
    setFormState(initialFormState);
    setIsCodeWorking(true);
  }, []);

  return {
    backupCodes,
    currentStep,
    formState,
    generateBackupCodes,
    hasBackupCodes: Boolean(user?.has_tokens_2fa) || backupCodes.length > 0,
    handleCancelSetup,
    handleBackupCodesAcknowledge,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleSubmit,
    handleToggleOtpKey,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    setRememberThisComputer,
    showRememberThisComputer,
    showOtpKey,
    startBackupCodeFlow,
    username: user?.email,
  };
};

export default useOtpFlow;
